import { zodResolver } from "@hookform/resolvers/zod";
import { httpsCallable } from "firebase/functions";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod/v3";
import { functions } from "../../lib/firebase";
import { trackEvent } from "../../lib/analytics";
import styles from "./UpdatesSignup.module.css";

const schema = z.object({
  email: z.string().email("Enter a valid email address"),
  // Honeypot — kept in the payload so the backend can decide; never validated
  // here, so a bot that fills it still "succeeds" from its point of view.
  companyUrl: z.string().optional(),
});

type UpdatesSignupValues = z.infer<typeof schema>;

interface UpdatesSignupProps {
  /** Where the form is rendered — recorded with the captured email. */
  source: "home" | "pricing" | "blog";
  className?: string;
  /**
   * Literal copy overrides, used instead of the i18n-backed defaults below.
   * The blog is English-only by design (see docs/seo-prerendering-scope.md
   * and how Industries/Changelog hardcode their own copy) — without this, a
   * visitor with a non-English browser locale would see this form's labels
   * translated while the rest of the blog page around it stayed in English.
   */
  copy?: Partial<{
    label: string;
    button: string;
    sending: string;
    success: string;
  }>;
}

const fallbackCopy = {
  label: "Not ready to try it? Get occasional updates on new features.",
  placeholder: "you@company.com",
  button: "Notify me",
  sending: "Adding...",
  success: "You're on the list - we'll send occasional product updates and nothing else.",
  error: "Something went wrong. Please try again.",
};

export default function UpdatesSignup({ source, className, copy }: UpdatesSignupProps) {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<UpdatesSignupValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", companyUrl: "" },
  });

  const onSubmit = async (values: UpdatesSignupValues) => {
    try {
      const subscribe = httpsCallable<
        { email: string; companyUrl?: string; source: string },
        { success: boolean }
      >(functions, "subscribeToUpdates");
      await subscribe({
        email: values.email,
        companyUrl: values.companyUrl ?? "",
        source,
      });
      trackEvent("updates_signup", { source });
      setSubmitted(true);
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : t("marketing.updatesSignup.error", { defaultValue: fallbackCopy.error });
      setError("email", { message });
    }
  };

  const fieldId = `updates-email-${source}`;

  return (
    <div className={className ? `${styles.card} ${className}` : styles.card}>
      {submitted ? (
        <p className={styles.success}>
          {copy?.success ?? t("marketing.updatesSignup.success", { defaultValue: fallbackCopy.success })}
        </p>
      ) : (
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
          <label className={styles.prompt} htmlFor={fieldId}>
            {copy?.label ?? t("marketing.updatesSignup.label", { defaultValue: fallbackCopy.label })}
          </label>
          <div className={styles.row}>
            <input
              id={fieldId}
              type="email"
              autoComplete="email"
              placeholder={t("marketing.updatesSignup.placeholder", {
                defaultValue: fallbackCopy.placeholder,
              })}
              className={styles.input}
              aria-invalid={Boolean(errors.email)}
              {...register("email")}
            />
            <button type="submit" className={styles.button} disabled={isSubmitting}>
              {isSubmitting
                ? copy?.sending ?? t("marketing.updatesSignup.sending", { defaultValue: fallbackCopy.sending })
                : copy?.button ?? t("marketing.updatesSignup.button", { defaultValue: fallbackCopy.button })}
            </button>
          </div>
          {/* Honeypot: hidden from real users, ignored by the schema. */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className={styles.honeypot}
            {...register("companyUrl")}
          />
          {errors.email && <p className={styles.error}>{errors.email.message}</p>}
        </form>
      )}
    </div>
  );
}
